import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  MenuItem,
  Box,
  IconButton,
  Paper,
  Typography,
  Grid,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
 
const shiftOptions = [
  {
    value: 'DayNight',
    label: 'Day, Night',
  },
  {
    value: 'MorningAfternoonNight',
    label: 'Morning, Afternoon, Night',
  },
];
 
const StepTwo = ({ supplierData, onSupplierDataChange }) => {
  const [error, setError] = useState(null);
 
  useEffect(() => {
    if (supplierData.length === 0) {
      onSupplierDataChange([{ name: '', id: '', shift: '', timeRanges: {} }]);
    }
  }, [supplierData, onSupplierDataChange]);
 
  const handleInputChange = (index, event) => {
    const { name, value } = event.target;
   
    // Allow only alphanumeric characters for name and id
    if (name === 'name' || name === 'id') {
      const alphanumericRegex = /^[a-zA-Z0-9]*$/;
      if (!alphanumericRegex.test(value)) {
        setError(`Please enter only alphanumeric characters for ${name === 'name' ? 'Supplier Name' : 'SAP ID'}.`);
        return;
      }
    }
 
    const updatedSuppliers = [...supplierData];
    updatedSuppliers[index][name] = value;
    setError(null); // Clear error when valid input is provided
    onSupplierDataChange(updatedSuppliers);
  };
 
  const handleAddSupplier = () => {
    const lastSupplier = supplierData[supplierData.length - 1];
 
    if (!lastSupplier.name || !lastSupplier.id || !lastSupplier.shift) {
      setError('Please fill in all fields for the current supplier before adding a new one.');
      return;
    }
 
    const requiredShifts = getRequiredShifts(lastSupplier.shift);
    const timeRanges = lastSupplier.timeRanges;
 
    for (const shift of requiredShifts) {
      if (!timeRanges[shift]?.start || !timeRanges[shift]?.end) {
        setError(`Please fill in all time ranges for the ${shift} shift.`);
        return;
      }
    }
 
    setError(null);
    onSupplierDataChange([...supplierData, { name: '', id: '', shift: '', timeRanges: {} }]);
  };
 
  const handleRemoveSupplier = (index) => {
    const updatedSuppliers = supplierData.filter((_, i) => i !== index);
    onSupplierDataChange(updatedSuppliers);
  };
 
  const getRequiredShifts = (shiftType) => {
    switch (shiftType) {
      case 'DayNight':
        return ['day', 'night'];
      case 'MorningAfternoonNight':
        return ['morning', 'afternoon', 'night'];
      default:
        return [];
    }
  };
 
  const parseTime = (time) => {
    if (!time) return null;
    const match = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;
    let [_, hours, minutes, period] = match;
    hours = parseInt(hours);
    minutes = parseInt(minutes);
    if (period.toUpperCase() === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }
    return hours * 60 + minutes;
  };
 
  const isOverlapping = (range1, range2) => {
    const start1 = parseTime(range1.start);
    const end1 = parseTime(range1.end);
    const start2 = parseTime(range2.start);
    const end2 = parseTime(range2.end);
 
    if (start1 === null || end1 === null || start2 === null || end2 === null) {
      return false;
    }
 
    return (
      (start1 < end2 && start2 < end1) ||
      (start1 === start2 && end1 === end2)
    );
  };
 
  const handleTimeRangeChange = (index, shiftPeriod, event) => {
    const { name, value } = event.target;
    const updatedSuppliers = [...supplierData];
    if (!updatedSuppliers[index].timeRanges) {
      updatedSuppliers[index].timeRanges = {};
    }
 
    updatedSuppliers[index].timeRanges[shiftPeriod] = {
      ...updatedSuppliers[index].timeRanges[shiftPeriod],
      [name]: value,
    };
 
    const currentRange = updatedSuppliers[index].timeRanges[shiftPeriod];
    const startTime = parseTime(currentRange.start);
    const endTime = parseTime(currentRange.end);
 
    if (startTime !== null && endTime !== null && endTime <= startTime) {
      setError(`The end time of the ${shiftPeriod} shift must be after the start time.`);
      return;
    }
 
    const timeRanges = updatedSuppliers[index].timeRanges;
    const otherShifts = Object.keys(timeRanges).filter((key) => key !== shiftPeriod);
    let hasOverlap = false;
    for (const shift of otherShifts) {
      if (timeRanges[shift].start && timeRanges[shift].end) {
        if (isOverlapping(currentRange, timeRanges[shift])) {
          hasOverlap = true;
          break;
        }
      }
    }
 
    if (hasOverlap) {
      setError(`The ${shiftPeriod} shift overlaps with another shift.`);
    } else {
      setError(null);
      onSupplierDataChange(updatedSuppliers);
    }
  };
 
  const renderTimePickers = (supplier, index) => {
    if (supplier.shift === 'DayNight') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper elevation={1} sx={{ p: 1 }}>
              <Typography variant="h7">Day Shift</Typography>
              <Box display="flex" gap={2} mt={0.5}>
                <TextField
                  label="Start"
                  type="time"
                  name="start"
                  value={supplier.timeRanges?.day?.start || ''}
                  onChange={(e) => handleTimeRangeChange(index, 'day', e)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  error={!!error}
                />
                <TextField
                  label="End"
                  type="time"
                  name="end"
                  value={supplier.timeRanges?.day?.end || ''}
                  onChange={(e) => handleTimeRangeChange(index, 'day', e)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  error={!!error}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper elevation={1} sx={{ p: 1 }}>
              <Typography variant="h7">Night Shift</Typography>
              <Box display="flex" gap={2} mt={0.5}>
                <TextField
                  label="Start"
                  type="time"
                  name="start"
                  value={supplier.timeRanges?.night?.start || ''}
                  onChange={(e) => handleTimeRangeChange(index, 'night', e)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  error={!!error}
                />
                <TextField
                  label="End"
                  type="time"
                  name="end"
                  value={supplier.timeRanges?.night?.end || ''}
                  onChange={(e) => handleTimeRangeChange(index, 'night', e)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  error={!!error}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      );
    } else if (supplier.shift === 'MorningAfternoonNight') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Paper elevation={1} sx={{ p: 1 }}>
              <Typography variant="h7">Morning Shift</Typography>
              <Box display="flex" gap={2} mt={0.5}>
                <TextField
                  label="Start"
                  type="time"
                  name="start"
                  value={supplier.timeRanges?.morning?.start || ''}
                  onChange={(e) => handleTimeRangeChange(index, 'morning', e)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  error={!!error}
                />
                <TextField
                  label="End"
                  type="time"
                  name="end"
                  value={supplier.timeRanges?.morning?.end || ''}
                  onChange={(e) => handleTimeRangeChange(index, 'morning', e)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  error={!!error}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper elevation={1} sx={{ p: 1 }}>
              <Typography variant="h7">Afternoon Shift</Typography>
              <Box display="flex" gap={2} mt={0.5}>
                <TextField
                  label="Start"
                  type="time"
                  name="start"
                  value={supplier.timeRanges?.afternoon?.start || ''}
                  onChange={(e) => handleTimeRangeChange(index, 'afternoon', e)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  error={!!error}
                />
                <TextField
                  label="End"
                  type="time"
                  name="end"
                  value={supplier.timeRanges?.afternoon?.end || ''}
                  onChange={(e) => handleTimeRangeChange(index, 'afternoon', e)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  error={!!error}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper elevation={1} sx={{ p: 1 }}>
              <Typography variant="h7">Night Shift</Typography>
              <Box display="flex" gap={2} mt={0.5}>
                <TextField
                  label="Start"
                  type="time"
                  name="start"
                  value={supplier.timeRanges?.night?.start || ''}
                  onChange={(e) => handleTimeRangeChange(index, 'night', e)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  error={!!error}
                />
                <TextField
                  label="End"
                  type="time"
                  name="end"
                  value={supplier.timeRanges?.night?.end || ''}
                  onChange={(e) => handleTimeRangeChange(index, 'night', e)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  error={!!error}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      );
    }
    return null;
  };
 
  return (
    <div>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {supplierData.map((supplier, index) => (
        <Box key={index} display="flex" flexDirection="column" mb={2} gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
              name="name"
              label={`Supplier Name ${index + 1}`}
              variant="outlined"
              value={supplier.name}
              onChange={(e) => handleInputChange(index, e)}
              fullWidth
            />
            <TextField
              name="id"
              label="SAP ID"
              variant="outlined"
              value={supplier.id}
              onChange={(e) => handleInputChange(index, e)}
              fullWidth
            />
            <TextField
              name="shift"
              label="Shift Type"
              select
              variant="outlined"
              value={supplier.shift}
              onChange={(e) => handleInputChange(index, e)}
              fullWidth
            >
              {shiftOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <IconButton onClick={() => handleRemoveSupplier(index)}>
              <DeleteIcon />
            </IconButton>
          </Box>
          {renderTimePickers(supplier, index)}
        </Box>
      ))}
      <Box mt={2} textAlign="center">
        <Button
          variant="contained"
          color="primary"
          style={{ backgroundColor: '#424752' }}
          onClick={handleAddSupplier}
        >
          Add Supplier
        </Button>
      </Box>
    </div>
  );
};
 
export default StepTwo;
 
// import React, { useEffect, useState } from 'react';
// import {
//   TextField,
//   Button,
//   MenuItem,
//   Box,
//   IconButton,
//   Paper,
//   Typography,
//   Grid,
//   Alert,
// } from '@mui/material';
// import DeleteIcon from '@mui/icons-material/Delete';

// const shiftOptions = [
//   {
//     value: 'DayNight',
//     label: 'Day, Night',
//   },
//   {
//     value: 'MorningAfternoonNight',
//     label: 'Morning, Afternoon, Night',
//   },
// ];

// const StepTwo = ({ supplierData, onSupplierDataChange }) => {
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     if (supplierData.length === 0) {
//       onSupplierDataChange([{ name: '', id: '', shift: '', timeRanges: {} }]);
//     }
//   }, [supplierData, onSupplierDataChange]);

//   const handleInputChange = (index, event) => {
//     const { name, value } = event.target;
//     const updatedSuppliers = [...supplierData];
//     updatedSuppliers[index][name] = value;
//     onSupplierDataChange(updatedSuppliers);
//   };

//   const handleAddSupplier = () => {
//     const lastSupplier = supplierData[supplierData.length - 1];

//     if (!lastSupplier.name || !lastSupplier.id || !lastSupplier.shift) {
//       setError('Please fill in all fields for the current supplier before adding a new one.');
//       return;
//     }

//     const requiredShifts = getRequiredShifts(lastSupplier.shift);
//     const timeRanges = lastSupplier.timeRanges;

//     for (const shift of requiredShifts) {
//       if (!timeRanges[shift]?.start || !timeRanges[shift]?.end) {
//         setError(`Please fill in all time ranges for the ${shift} shift.`);
//         return;
//       }
//     }

//     setError(null);
//     onSupplierDataChange([...supplierData, { name: '', id: '', shift: '', timeRanges: {} }]);
//   };

//   const handleRemoveSupplier = (index) => {
//     const updatedSuppliers = supplierData.filter((_, i) => i !== index);
//     onSupplierDataChange(updatedSuppliers);
//   };

//   const getRequiredShifts = (shiftType) => {
//     switch (shiftType) {
//       case 'DayNight':
//         return ['day', 'night'];
//       case 'MorningAfternoonNight':
//         return ['morning', 'afternoon', 'night'];
//       default:
//         return [];
//     }
//   };

//   const parseTime = (time) => {
//     if (!time) return null;
//     const match = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
//     if (!match) return null;
//     let [_, hours, minutes, period] = match;
//     hours = parseInt(hours);
//     minutes = parseInt(minutes);
//     if (period.toUpperCase() === 'PM' && hours !== 12) {
//       hours += 12;
//     } else if (period.toUpperCase() === 'AM' && hours === 12) {
//       hours = 0;
//     }
//     return hours * 60 + minutes;
//   };

//   const isOverlapping = (range1, range2) => {
//     const start1 = parseTime(range1.start);
//     const end1 = parseTime(range1.end);
//     const start2 = parseTime(range2.start);
//     const end2 = parseTime(range2.end);

//     if (start1 === null || end1 === null || start2 === null || end2 === null) {
//       return false;
//     }

//     return (
//       (start1 < end2 && start2 < end1) ||
//       (start1 === start2 && end1 === end2)
//     );
//   };

//   const handleTimeRangeChange = (index, shiftPeriod, event) => {
//     const { name, value } = event.target;
//     const updatedSuppliers = [...supplierData];
//     if (!updatedSuppliers[index].timeRanges) {
//       updatedSuppliers[index].timeRanges = {};
//     }

//     updatedSuppliers[index].timeRanges[shiftPeriod] = {
//       ...updatedSuppliers[index].timeRanges[shiftPeriod],
//       [name]: value,
//     };

//     const currentRange = updatedSuppliers[index].timeRanges[shiftPeriod];
//     const startTime = parseTime(currentRange.start);
//     const endTime = parseTime(currentRange.end);

//     if (startTime !== null && endTime !== null && endTime <= startTime) {
//       setError(`The end time of the ${shiftPeriod} shift must be after the start time.`);
//       return;
//     }

//     const timeRanges = updatedSuppliers[index].timeRanges;
//     const otherShifts = Object.keys(timeRanges).filter((key) => key !== shiftPeriod);
//     let hasOverlap = false;
//     for (const shift of otherShifts) {
//       if (timeRanges[shift].start && timeRanges[shift].end) {
//         if (isOverlapping(currentRange, timeRanges[shift])) {
//           hasOverlap = true;
//           break;
//         }
//       }
//     }

//     if (hasOverlap) {
//       setError(`The ${shiftPeriod} shift overlaps with another shift.`);
//     } else {
//       setError(null);
//       onSupplierDataChange(updatedSuppliers);
//     }
//   };

//   const renderTimePickers = (supplier, index) => {
//     if (supplier.shift === 'DayNight') {
//       return (
//         <Grid container spacing={2}>
//           <Grid item xs={12} sm={6}>
//             <Paper elevation={1} sx={{ p: 1 }}>
//               <Typography variant="h7">Day Shift</Typography>
//               <Box display="flex" gap={2} mt={0.5}>
//                 <TextField
//                   label="Start"
//                   type="time"
//                   name="start"
//                   value={supplier.timeRanges?.day?.start || ''}
//                   onChange={(e) => handleTimeRangeChange(index, 'day', e)}
//                   fullWidth
//                   InputLabelProps={{ shrink: true }}
//                   inputProps={{ step: 300 }}
//                   error={!!error}
//                 />
//                 <TextField
//                   label="End"
//                   type="time"
//                   name="end"
//                   value={supplier.timeRanges?.day?.end || ''}
//                   onChange={(e) => handleTimeRangeChange(index, 'day', e)}
//                   fullWidth
//                   InputLabelProps={{ shrink: true }}
//                   inputProps={{ step: 300 }}
//                   error={!!error}
//                 />
//               </Box>
//             </Paper>
//           </Grid>
//           <Grid item xs={12} sm={6}>
//             <Paper elevation={1} sx={{ p: 1 }}>
//               <Typography variant="h7">Night Shift</Typography>
//               <Box display="flex" gap={2} mt={0.5}>
//                 <TextField
//                   label="Start"
//                   type="time"
//                   name="start"
//                   value={supplier.timeRanges?.night?.start || ''}
//                   onChange={(e) => handleTimeRangeChange(index, 'night', e)}
//                   fullWidth
//                   InputLabelProps={{ shrink: true }}
//                   inputProps={{ step: 300 }}
//                   error={!!error}
//                 />
//                 <TextField
//                   label="End"
//                   type="time"
//                   name="end"
//                   value={supplier.timeRanges?.night?.end || ''}
//                   onChange={(e) => handleTimeRangeChange(index, 'night', e)}
//                   fullWidth
//                   InputLabelProps={{ shrink: true }}
//                   inputProps={{ step: 300 }}
//                   error={!!error}
//                 />
//               </Box>
//             </Paper>
//           </Grid>
//         </Grid>
//       );
//     } else if (supplier.shift === 'MorningAfternoonNight') {
//       return (
//         <Grid container spacing={2}>
//           <Grid item xs={12} sm={4}>
//             <Paper elevation={1} sx={{ p: 1 }}>
//               <Typography variant="h7">Morning Shift</Typography>
//               <Box display="flex" gap={2} mt={0.5}>
//                 <TextField
//                   label="Start"
//                   type="time"
//                   name="start"
//                   value={supplier.timeRanges?.morning?.start || ''}
//                   onChange={(e) => handleTimeRangeChange(index, 'morning', e)}
//                   fullWidth
//                   InputLabelProps={{ shrink: true }}
//                   inputProps={{ step: 300 }}
//                   error={!!error}
//                 />
//                 <TextField
//                   label="End"
//                   type="time"
//                   name="end"
//                   value={supplier.timeRanges?.morning?.end || ''}
//                   onChange={(e) => handleTimeRangeChange(index, 'morning', e)}
//                   fullWidth
//                   InputLabelProps={{ shrink: true }}
//                   inputProps={{ step: 300 }}
//                   error={!!error}
//                 />
//               </Box>
//             </Paper>
//           </Grid>
//           <Grid item xs={12} sm={4}>
//             <Paper elevation={1} sx={{ p: 1 }}>
//               <Typography variant="h7">Afternoon Shift</Typography>
//               <Box display="flex" gap={2} mt={0.5}>
//                 <TextField
//                   label="Start"
//                   type="time"
//                   name="start"
//                   value={supplier.timeRanges?.afternoon?.start || ''}
//                   onChange={(e) => handleTimeRangeChange(index, 'afternoon', e)}
//                   fullWidth
//                   InputLabelProps={{ shrink: true }}
//                   inputProps={{ step: 300 }}
//                   error={!!error}
//                 />
//                 <TextField
//                   label="End"
//                   type="time"
//                   name="end"
//                   value={supplier.timeRanges?.afternoon?.end || ''}
//                   onChange={(e) => handleTimeRangeChange(index, 'afternoon', e)}
//                   fullWidth
//                   InputLabelProps={{ shrink: true }}
//                   inputProps={{ step: 300 }}
//                   error={!!error}
//                 />
//               </Box>
//             </Paper>
//           </Grid>
//           <Grid item xs={12} sm={4}>
//             <Paper elevation={1} sx={{ p: 1 }}>
//               <Typography variant="h7">Night Shift</Typography>
//               <Box display="flex" gap={2} mt={0.5}>
//                 <TextField
//                   label="Start"
//                   type="time"
//                   name="start"
//                   value={supplier.timeRanges?.night?.start || ''}
//                   onChange={(e) => handleTimeRangeChange(index, 'night', e)}
//                   fullWidth
//                   InputLabelProps={{ shrink: true }}
//                   inputProps={{ step: 300 }}
//                   error={!!error}
//                 />
//                 <TextField
//                   label="End"
//                   type="time"
//                   name="end"
//                   value={supplier.timeRanges?.night?.end || ''}
//                   onChange={(e) => handleTimeRangeChange(index, 'night', e)}
//                   fullWidth
//                   InputLabelProps={{ shrink: true }}
//                   inputProps={{ step: 300 }}
//                   error={!!error}
//                 />
//               </Box>
//             </Paper>
//           </Grid>
//         </Grid>
//       );
//     }
//     return null;
//   };

//   return (
//     <div>
//       {error && (
//         <Alert severity="error" sx={{ mb: 2 }}>
//           {error}
//         </Alert>
//       )}
//       {supplierData.map((supplier, index) => (
//         <Box key={index} display="flex" flexDirection="column" mb={2} gap={2}>
//           <Box display="flex" alignItems="center" gap={2}>
//             <TextField
//               name="name"
//               label={`Supplier Name ${index + 1}`}
//               variant="outlined"
//               value={supplier.name}
//               onChange={(e) => handleInputChange(index, e)}
//               fullWidth
//             />
//             <TextField
//               name="id"
//               label="SAP ID"
//               variant="outlined"
//               value={supplier.id}
//               onChange={(e) => handleInputChange(index, e)}
//               fullWidth
//             />
//             <TextField
//               name="shift"
//               label="Shift Type"
//               select
//               variant="outlined"
//               value={supplier.shift}
//               onChange={(e) => handleInputChange(index, e)}
//               fullWidth
//             >
//               {shiftOptions.map((option) => (
//                 <MenuItem key={option.value} value={option.value}>
//                   {option.label}
//                 </MenuItem>
//               ))}
//             </TextField>
//             <IconButton onClick={() => handleRemoveSupplier(index)}>
//               <DeleteIcon />
//             </IconButton>
//           </Box>
//           {renderTimePickers(supplier, index)}
//         </Box>
//       ))}
//       <Box mt={2} textAlign="center">
//         <Button
//           variant="contained"
//           color="primary"
//           style={{ backgroundColor: '#424752' }}
//           onClick={handleAddSupplier}
//         >
//           Add Supplier
//         </Button>
//       </Box>
//     </div>
//   );
// };

// export default StepTwo;